<template>
  <div v-if="isLoadingFetch" class="flex items-center gap-1.5 text-zinc-500">
    <Icon class="animate-spin text-xl" name="progress_activity" />
    <span>{{ __('Loading...') }}</span>
  </div>

  <div v-else class="space-y-6 md:space-y-8">
    <div class="flex items-center gap-2 md:gap-4">
      <Icon class="!text-3xl text-primary" name="badge" type="filled" />
      <h1 class="text-2xl font-medium text-zinc-800 md:text-4xl">
        {{ __('Let’s get to know you better') }}
      </h1>
    </div>

    <form class="space-y-8" @submit.prevent="handleSubmit">
      <InputLocation
        v-if="fields.location.visible"
        v-model:value="fields.location.value"
        v-model:errors="fields.location.errors"
        :label-country="__('Country of residence')"
        :label-city="__('City of residence')"
        :placeholder-country="__('The country you live in')"
        :placeholder-city="__('The city you live in')"
        :options="fields.location.options"
        :required="fields.location.mandatory"
      />

      <InputSkills
        v-if="fields.skills.visible"
        id="skills"
        v-model:value="fields.skills.value"
        v-model:errors="fields.skills.errors"
        :label="__('Select your skills from the list below.')"
        :placeholder="__('Add skills')"
        :required="fields.skills.mandatory"
        :is-new-options-allowed="false"
      >
        <template #icon>
          <Icon name="stylus_note" />
        </template>
      </InputSkills>

      <div class="grid gap-4 md:grid-cols-2">
        <InputCombobox
          v-if="fields.education_level.visible"
          id="education_level"
          v-model:value="fields.education_level.value"
          v-model:errors="fields.education_level.errors"
          :label="__('What’s your highest level of education?')"
          :placeholder="__('Enter level of education')"
          :required="fields.education_level.mandatory"
          :options="fields.education_level.options"
        />

        <InputText
          v-if="fields.field_of_study.visible"
          id="field_of_study"
          v-model:value="fields.field_of_study.value"
          v-model:errors="fields.field_of_study.errors"
          :label="__('What was your major or field of study?')"
          :placeholder="__('Field of study')"
          :required="fields.field_of_study.mandatory"
        />
      </div>

      <div class="flex items-center justify-between">
        <Button
          class="flex items-center gap-1.5"
          variant="ghost"
          @click="handleBackButtonClick"
        >
          <Icon class="text-lg" name="arrow_back_ios_new" />
          <span>{{ __('Go Back') }}</span>
        </Button>

        <Button
          class="ml-auto flex items-center gap-1.5"
          type="submit"
          :disabled="isLoadingUpdate"
        >
          <Icon
            v-if="isLoadingUpdate"
            class="animate-spin text-lg"
            name="progress_activity"
          />

          <span>{{ __('Next') }}</span>
        </Button>
      </div>
    </form>
  </div>
</template>

<script setup>
  import { onMounted, ref } from 'vue';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import { __ } from '@/helpers';
  import { Button } from '@/components/block/button';

  import { InputCombobox } from '@/components/form/input-combobox';
  import { InputLocation } from '@/components/form/input-location';
  import { InputSkills } from '@/components/form/input-skills';
  import { InputText } from '@/components/form/input-text';
  import Icon from '@/components/global/Icon.vue';

  const emits = defineEmits(['back', 'next']);

  const toast = useToast();

  const fields = ref({
    location: {
      visible: false,
      mandatory: false,
      value: null,
      errors: [],
      options: []
    },
    skills: {
      visible: false,
      mandatory: false,
      value: [],
      errors: [],
      options: []
    },
    education_level: {
      visible: false,
      mandatory: false,
      value: null,
      errors: [],
      options: []
    },
    field_of_study: {
      visible: false,
      mandatory: false,
      value: null,
      errors: []
    }
  });
  const isLoadingFetch = ref(false);
  const isLoadingUpdate = ref(false);

  function isValid(value) {
    if (!value) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'object') {
      for (const prop in value) {
        if (value[prop] === null) {
          return false;
        }
      }
      return true;
    }

    return true;
  }

  async function fetchData() {
    isLoadingFetch.value = true;

    try {
      const response = await axios.get(
        route('onboarding.step', { slug: 'details' })
      );

      Object.entries(response.data).forEach(([key, value]) => {
        if (fields.value[key]) {
          fields.value[key] = { ...fields.value[key], ...value };
        }
      });
    } catch {
      toast.error(
        __('An unexpected error has occured. Please try again later.')
      );
    }

    isLoadingFetch.value = false;
  }

  function handleBackButtonClick() {
    emits('back');
  }

  async function handleSubmit() {
    const requiredFieldKeys = Object.keys(fields.value).filter(
      (key) => fields.value[key].visible && fields.value[key].mandatory
    );
    let firstInvalidField;

    requiredFieldKeys.forEach((key) => {
      if (!isValid(fields.value[key].value)) {
        fields.value[key].errors = [__('This field is required')];

        const element = document.getElementById(key);

        if (element && !firstInvalidField) {
          firstInvalidField = element;
        }
      }
    });

    if (
      Object.values(fields.value).some(
        (field) => field.errors && field.errors.length
      )
    ) {
      if (firstInvalidField) {
        firstInvalidField.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      return;
    }

    isLoadingUpdate.value = true;

    try {
      const newSkillPromiseList = fields.value.skills.value
        .filter((skill) => !skill.id)
        .map((skill) => {
          return axios.post(route('redesign.cv.skill.create'), {
            name: skill.name
          });
        });

      const newSkillList = (await Promise.all(newSkillPromiseList)).map(
        (item) => ({ id: item.data.id, name: item.data.name })
      );

      if (newSkillList) {
        fields.value.skills.value = fields.value.skills.value.map((skill) => {
          const newSkillOption = newSkillList.find(
            (newSkill) => newSkill.name === skill.name
          );

          if (newSkillOption) {
            return newSkillOption;
          }

          return skill;
        });
      }
    } catch {
      toast.error(
        __('An unexpected error has occured. Please try again later.')
      );
    }

    const payload = Object.keys(fields.value).reduce((acc, key) => {
      if (fields.value[key].visible) {
        acc[key] = fields.value[key].value;
      }
      return acc;
    }, {});

    try {
      await axios.post(route('onboarding.step', { slug: 'details' }), payload);
      emits('next');
    } catch (error) {
      if (error?.response?.data?.errors) {
        Object.keys(error.response.data.errors).forEach((key) => {
          if (fields.value[key]) {
            fields.value[key].errors = error.response.data.errors[key];
          }
        });
      } else {
        toast.error(
          __('An unexpected error has occured. Please try again later.')
        );
      }
    }

    isLoadingUpdate.value = false;
  }

  onMounted(async () => {
    await fetchData();
  });
</script>
